import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, Phone } from 'lucide-react';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Primary navigation
  const navLinks = [
    { label: 'About Us', href: '/about/' },
    { label: 'FAQ', href: '/faq/' },
    { label: 'Blog', href: '/blog/' },
    { label: 'Popular Routes', href: '/#routes' },
    { label: 'Contact', href: '/contact/' },
  ];

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    
    if (href.startsWith('/#')) {
      const sectionId = href.substring(2);
      
      if (location.pathname === '/') {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        navigate('/');
        setTimeout(() => {
          const element = document.getElementById(sectionId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    } else {
      navigate(href);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-black border-b border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Logo - Left */}
          <a href="/" className="flex-shrink-0">
            <img 
              src="https://customer-assets.emergentagent.com/job_27fa83fe-13fc-43b1-921c-bb11a32684e7/artifacts/g9kwyws1_go-turkey-transfer-header.png" 
              alt="GoTurkeyTransfer" 
              className="h-14 sm:h-16 md:h-20 w-auto"
            />
          </a>

          {/* Desktop Navigation - Center */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-white/70 hover:text-white font-medium text-sm tracking-wide transition-colors cursor-pointer"
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Desktop CTA - Right */}
          <div className="hidden lg:flex items-center gap-6">
            <a
              href="https://wa.me/905352385797"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-white/60 hover:text-white text-sm transition-colors"
            >
              <Phone className="h-4 w-4" />
              <span className="hidden xl:inline">+90 535 238 5797</span>
            </a>
            <Link to="/">
              <Button 
                size="default"
                className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-6"
                data-testid="header-book-now-btn"
              >
                Book Now
              </Button>
            </Link>
          </div>

          {/* Mobile: Phone + Menu Toggle */}
          <div className="flex lg:hidden items-center gap-3">
            <a
              href="https://wa.me/905352385797"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-white/70 hover:text-white transition-colors"
              aria-label="WhatsApp"
            >
              <Phone className="h-5 w-5" />
            </a>
            <button
              className="p-2 text-white"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-800 animate-fade-in-up">
            <nav className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className="text-white/80 hover:text-white font-medium py-3 px-2 rounded-lg hover:bg-white/5 transition-colors cursor-pointer"
                >
                  {link.label}
                </a>
              ))}
              <div className="pt-4 mt-2 border-t border-gray-800">
                <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>
                  <Button 
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold py-6"
                    data-testid="mobile-book-now-btn"
                  >
                    Book Now
                  </Button>
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;